import React, { useState } from "react";
import {
  ActionIcon,
  Popover,
  SimpleGrid,
  Text,
  UnstyledButton,
} from "@mantine/core";
import styles from "./EmojiPicker.module.css";

/** One pickable emoji. `label` is its accessible name (and tooltip). */
export interface EmojiOption {
  emoji: string;
  label: string;
}

export interface EmojiPickerProps {
  /** Selected emoji; null = the default icon. */
  value: string | null;
  onChange: (emoji: string | null) => void;
  /** The emojis to offer, in grid order. */
  options: EmojiOption[];
  /** Shown on the trigger and the reset row when no emoji is picked. */
  defaultIcon: React.ReactNode;
  /** Accessible name of the trigger button. */
  ariaLabel: string;
  /** Trigger tooltip. Default "Choose an icon". */
  title?: string;
  /** Label of the reset row. Default "Default". */
  defaultLabel?: string;
  disabled?: boolean;
}

/**
 * Round emoji button meant for an input's left section (TextInput
 * `leftSection`, with `leftSectionPointerEvents="all"`): shows the selected
 * emoji (or `defaultIcon`) and opens a curated 8-column emoji grid with a
 * "Default" reset on top. On phones the grid drops to 6 columns of 44px touch
 * targets. Used by appointment tags (TagEmojiPicker) and checkout reminders.
 */
export default function EmojiPicker({
  value,
  onChange,
  options,
  defaultIcon,
  ariaLabel,
  title = "Choose an icon",
  defaultLabel = "Default",
  disabled = false,
}: EmojiPickerProps) {
  const [opened, setOpened] = useState(false);

  const pick = (emoji: string | null) => {
    onChange(emoji);
    setOpened(false);
  };

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-start"
      shadow="md"
      radius="md"
      withinPortal
    >
      <Popover.Target>
        <ActionIcon
          variant={opened ? "light" : "subtle"}
          color="teal"
          radius="xl"
          size="md"
          disabled={disabled}
          aria-label={ariaLabel}
          aria-haspopup="dialog"
          aria-expanded={opened}
          title={title}
          onClick={() => setOpened((o) => !o)}
        >
          {value ? (
            <Text component="span" className={styles.pickerCurrent}>
              {value}
            </Text>
          ) : (
            defaultIcon
          )}
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown p={6}>
        <UnstyledButton
          className={`${styles.pickerDefault} ${
            value ? "" : styles.pickerSelected
          }`}
          aria-pressed={!value}
          onClick={() => pick(null)}
        >
          {defaultIcon}
          <Text component="span" size="sm" fw={600}>
            {defaultLabel}
          </Text>
        </UnstyledButton>
        <SimpleGrid
          cols={{ base: 6, sm: 8 }}
          spacing={2}
          verticalSpacing={2}
          mt={4}
        >
          {options.map((option) => (
            <UnstyledButton
              key={option.emoji}
              className={`${styles.pickerEmoji} ${
                value === option.emoji ? styles.pickerSelected : ""
              }`}
              aria-label={option.label}
              aria-pressed={value === option.emoji}
              title={option.label}
              onClick={() => pick(option.emoji)}
            >
              {option.emoji}
            </UnstyledButton>
          ))}
        </SimpleGrid>
      </Popover.Dropdown>
    </Popover>
  );
}
